// Money formatters. Storage = integer minor units (centimes/cents).
//
// Diaspora-currency display: USD primary (most US-based), CAD/EUR/GBP
// alongside for Canada / France+EU / UK markets. FX rates here are
// approximate, refreshed in this file; quote-time fx_rate_htg_per_usd
// snapshot is what drives the actual customer charge so display drift
// is non-critical.

export type DisplayCurrency = 'USD' | 'CAD' | 'EUR' | 'GBP';

// Approximate cross-rates (USD per local unit) → multiply USD * rate
// to convert into the local currency. Updated 2026-06; quote-time
// snapshot is authoritative.
const RATES: Record<DisplayCurrency, { perUsd: number; symbol: string; locale: string }> = {
  USD: { perUsd: 1.00,    symbol: '$',  locale: 'en-US' },
  CAD: { perUsd: 1.37,    symbol: 'C$', locale: 'en-CA' },
  EUR: { perUsd: 0.92,    symbol: '€',  locale: 'fr-FR' },
  GBP: { perUsd: 0.78,    symbol: '£',  locale: 'en-GB' },
};

const APPROX_HTG_PER_USD = 132.4;

// localStorage key for the user's chosen display currency.
const STORAGE_KEY = 'lakaycart_currency';

export function getDisplayCurrency(): DisplayCurrency {
  if (typeof window === 'undefined') return 'USD';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && stored in RATES) return stored as DisplayCurrency;
  // Auto-detect from browser locale on first visit. Falls back to USD.
  const lang = window.navigator?.language ?? '';
  if (lang.startsWith('fr-CA') || lang.startsWith('en-CA')) return 'CAD';
  if (lang.startsWith('en-GB')) return 'GBP';
  if (lang.startsWith('fr') || lang.startsWith('de') || lang.startsWith('es') || lang.startsWith('it')) return 'EUR';
  return 'USD';
}

export function setDisplayCurrency(c: DisplayCurrency): void {
  if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, c);
}

export const SUPPORTED_CURRENCIES: DisplayCurrency[] = ['USD', 'CAD', 'EUR', 'GBP'];

// ─── Formatters ──────────────────────────────────────────────

export function formatHtgMinor(minor: number, opts?: { withSymbol?: boolean }): string {
  const gourdes = minor / 100;
  const formatted = gourdes.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return opts?.withSymbol === false ? formatted : `HTG ${formatted}`;
}

/** Render HTG amount in the viewer's chosen display currency. */
export function formatDisplayFromHtgMinor(minor: number, currency?: DisplayCurrency): string {
  const c = currency ?? getDisplayCurrency();
  const rate = RATES[c];
  const usd = (minor / 100) / APPROX_HTG_PER_USD;
  const local = usd * rate.perUsd;
  return `${rate.symbol}${local.toLocaleString(rate.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Backwards-compat — USD-only helper kept for older callers. */
export function formatUsdFromHtgMinor(minor: number): string {
  return formatDisplayFromHtgMinor(minor, 'USD');
}

/** HTG + parenthesized display-currency, used on item cards. */
export function formatHtgPair(minor: number, currency?: DisplayCurrency): string {
  return `${formatHtgMinor(minor)} (≈ ${formatDisplayFromHtgMinor(minor, currency)})`;
}

// ─── Delivery ETA badges ────────────────────────────────────

export function deliveryEtaLabel(hours: number | null | undefined): { tone: 'emerald' | 'sky' | 'amber' | 'slate'; label: string } | null {
  if (hours == null) return null;
  if (hours <= 2)  return { tone: 'emerald', label: `~${hours}h delivery` };
  if (hours <= 8)  return { tone: 'sky',     label: `Same-day · ~${hours}h` };
  if (hours <= 24) return { tone: 'amber',   label: 'Next-day delivery' };
  return { tone: 'slate', label: `~${Math.round(hours / 24)}d delivery` };
}
